import React from 'react';
import { Box, Card, CardActionArea, CardContent, Chip, Stack, Typography } from '@mui/material';

import type { TrendStory } from '../types';
import {
  CARD_HOVER_SX,
  CARD_RADIUS,
  CARD_INGRESS_SX,
  STORY_CARD_TITLE_SX,
  TREND_COLORS,
  getFallbackArticleImage,
  getArticleImage,
  getDesignSubCategoryLabel,
} from '../trendTheme';

type TrendStoryCardProps = {
  story: TrendStory;
  onOpen?: (story: TrendStory) => void;
};

export default function TrendStoryCard({ story, onOpen }: TrendStoryCardProps) {
  const imageUrl = getArticleImage(story.heroImage, story.category);
  const fallbackImageUrl = getFallbackArticleImage(story.category);

  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: CARD_RADIUS,
        overflow: 'hidden',
        border: 'none',
        boxShadow: '0 2px 12px rgba(7,47,83,0.06)',
        ...CARD_HOVER_SX,
      }}
    >
      <CardActionArea
        onClick={() => onOpen?.(story)}
        sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}
      >
        <Box sx={{ position: 'relative', pt: '58%', overflow: 'hidden' }}>
          <Box
            component="img"
            src={imageUrl}
            alt=""
            onError={(event: React.SyntheticEvent<HTMLImageElement>) => {
              if (event.currentTarget.src !== fallbackImageUrl) {
                event.currentTarget.src = fallbackImageUrl;
              }
            }}
            sx={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              background: 'linear-gradient(to top, rgba(7,47,83,0.78) 0%, rgba(7,47,83,0.05) 65%)',
            }}
          />
          <Stack direction="row" spacing={0.75} sx={{ position: 'absolute', left: 12, right: 12, bottom: 12, flexWrap: 'wrap' }} useFlexGap>
            <Chip
              label={(story.storyType || story.category || 'berättelse').toUpperCase()}
              size="small"
              sx={{
                bgcolor: TREND_COLORS.gold,
                color: TREND_COLORS.navy,
                fontWeight: 800,
                fontSize: '0.62rem',
                letterSpacing: '0.12em',
                borderRadius: 1,
              }}
            />
            {story.designSubCategory && (
              <Chip
                label={getDesignSubCategoryLabel(story.designSubCategory)}
                size="small"
                sx={{
                  bgcolor: 'rgba(255,255,255,0.18)',
                  color: '#fff',
                  backdropFilter: 'blur(4px)',
                  fontWeight: 700,
                  fontSize: '0.62rem',
                }}
              />
            )}
            {(story.provenance === 'discovery' || story.provenance === 'feed+discovery') && (
              <Chip
                label={story.provenance === 'feed+discovery' ? 'FEED + DISCOVERY' : 'DISCOVERY'}
                size="small"
                sx={{
                  bgcolor: TREND_COLORS.terracotta,
                  color: '#fff',
                  fontWeight: 700,
                  fontSize: '0.55rem',
                  letterSpacing: '0.06em',
                  borderRadius: 1,
                }}
              />
            )}
          </Stack>
        </Box>

        <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', p: 2.5 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
            <Typography sx={{ color: TREND_COLORS.textMuted, fontSize: '0.72rem', letterSpacing: '0.08em', textTransform: 'uppercase', fontWeight: 700 }}>
              {story.articleCount} artiklar
            </Typography>
            <Typography sx={{ color: TREND_COLORS.olive, fontSize: '0.76rem', fontWeight: 700 }}>
              Temapoäng {story.storyScore}
            </Typography>
          </Stack>

          <Typography
            sx={{
              ...STORY_CARD_TITLE_SX,
              mb: 1.25,
            }}
          >
            {story.title}
          </Typography>

          <Typography
            variant="body2"
            sx={{
              ...CARD_INGRESS_SX,
              flex: 1,
              display: '-webkit-box',
              WebkitLineClamp: 6,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              mb: 1.5,
            }}
          >
            {story.summary || story.whatItMeansForUs}
          </Typography>

          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
            {[...story.audienceSegments, ...story.consumerBehaviors, ...story.usageOccasions]
              .slice(0, 3)
              .map((entry) => (
                <Chip
                  key={entry}
                  label={entry}
                  size="small"
                  variant="outlined"
                  sx={{ fontSize: '0.65rem', borderColor: TREND_COLORS.divider, color: TREND_COLORS.textMuted }}
                />
              ))}
          </Stack>
          <Typography variant="caption" sx={{ mt: 1.5, fontWeight: 800, color: TREND_COLORS.navy }}>
            Öppna analys
          </Typography>
        </CardContent>
      </CardActionArea>
    </Card>
  );
}

